/**
 * /app/metrics — Analytics MVP Food Market
 * Period picker · KPIs con comparativo · ticket promedio · cohort clientes
 * · top productos · serie diaria revenue+orders · estado de órdenes
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import {
  ClinicalPageShell,
  ClinicalSection,
  PageSkeleton,
} from "@/components/clinical";
import { useTenantContext } from "@/lib/tenant-context";
import { useReportingLocationScope } from "@/hooks/use-reporting-location-scope";
import { Banknote,
  ArrowDownRight,
  ArrowUpRight,
  HeartHandshake,
  Minus,
  ShoppingBasket,
  TrendingUp,
  Users,
  Wallet,
  Truck,
  Package,
  Zap,
  Mail,
  Trash2,
  Plus
} from "lucide-react";
import type { AdSpend } from "@/lib/db-service/ad-spends";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { ORDER_STATUS_LABELS } from "@/domain/sm-models";
import type { OrderStatus } from "@/domain/sm-models";
import {
  marketingSpendCentsToUnits,
  marketingSpendUnitsToCents,
} from "@/lib/metrics-ltv";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  modulePanelClass,
  resolveInitialAnalyticsModule,
  visibleAnalyticsModules,
  type AnalyticsModuleNavItem,
} from "@/lib/analytics-module-nav";
import type { AnalyticsMetricModuleId } from "@/lib/analytics-metric-catalog";

export const Route = createFileRoute("/app/metrics")({
  component: MetricsPage,
  head: () => ({ meta: [{ title: "Métricas — Structura Market" }] }),
});

type Period = "7d" | "30d" | "90d" | "ytd";

interface MetricsData {
  period: Period;
  periodStart: string;
  periodEnd: string;
  ordersToday: number;
  ordersPeriod: number;
  revenuePeriod: number;
  ordersPending: number;
  ticketPromedio: number;
  ordersPrevPeriod: number;
  revenuePrevPeriod: number;
  ticketPrevPeriod: number;
  newCustomers: number;
  returningCustomers: number;
  totalUniqueCustomers: number;
  repeatCustomerCount: number;
  repeatCustomerRate: number;
  metricGates?: {
    executive?: boolean;
    sales?: boolean;
    customers?: boolean;
    storefront?: boolean;
    conversion?: boolean;
    products?: boolean;
    categories?: boolean;
    marketing?: boolean;
    logistics?: boolean;
    pos?: boolean;
    salon?: boolean;
    inventory?: boolean;
    emails?: boolean;
    automations?: boolean;
    kpiBounce?: boolean;
    kpiLtv?: boolean;
    kpiCac?: boolean;
    kpiRfm?: boolean;
    kpiQrMesa?: boolean;
    kpiOtif?: boolean;
    kpiOtifContractual?: boolean;
    kpiOtifInFull?: boolean;
    kpiBom?: boolean;
  };
  funnel?: {
    sessions: number;
    pageViews?: number;
    scrollDepthEvents?: number;
    timeOnPageEvents?: number;
    pageExits?: number;
    bounces?: number;
    bounceRate?: number;
    heroCtaClicks?: number;
    bannerClicks?: number;
    carouselViews?: number;
    wishlistOpens?: number;
    cartUpdates?: number;
    productViews: number;
    categoryViews?: number;
    searches?: number;
    searchZeros?: number;
    addToCarts: number;
    removeFromCarts?: number;
    checkoutsStarted: number;
    orders: number;
    viewToCartRate: number;
    cartToCheckoutRate: number;
    checkoutToOrderRate: number;
    sessionToOrderRate: number;
  };
  abandonedCheckout?: {
    pendingPayment: number;
    recoveryEmailsSent: number;
    awaitingRecovery: number;
    pendingRevenue: number;
    abandonedRate: number;
    emailDelivered?: number;
    emailOpened?: number;
    emailClicked?: number;
    openRate?: number;
    clickRate?: number;
  };
  rfm?: {
    active: number;
    atRisk: number;
    hibernating: number;
  };
  qrMesa?: {
    scans: number;
  };
  ltv?: {
    scope: "all_time";
    average: number;
    median: number | null;
    customersWithOrders: number;
    totalRevenue: number;
    periodAverage: number;
    periodMedian: number | null;
    periodCustomers: number;
    periodLabel: string;
    buckets?: Array<{ label: string; min: number; max: number | null; count: number }>;
    top?: Array<{ customerKey: string; revenue: number; orderCount: number }>;
  };
  cac?: number | null;
  cacNote?: string;
  marketingSpendCentsMonthly?: number | null;
  adsSpendMetaCents?: number | null;
  adsSpendGoogleCents?: number | null;
  marketingSpendCentsTotal?: number | null;
  marketingSpend?: number | null;
  referralAttributedOrders?: number;
  reviews?: {
    npsSubmitted: number;
    reviewSubmitted: number;
    reviewRequested: number;
    reviewLinkClicked?: number;
  };
  /** SCRUM-222 — eventos Growth del mes (sm_usage_events). */
  growthEventCounts?: { impressions: number; interactions: number };
  campaignRevenue?: Array<{
    key: string;
    campaignId: string | null;
    couponCode: string | null;
    orders: number;
    revenue: number;
  }>;
  topProducts: { name: string; qty: number; revenue: number }[];
  ordersByDay: { day: string; count: number; revenue: number }[];
  ordersByStatus: { status: string; count: number }[];
  pos?: {
    openSessions: number;
    sessionsClosed: number;
    tickets: number;
    revenue: number;
    ticketAvg: number;
    absDifference: number;
    avgAbsDifference: number;
  };
  logistics?: {
    deliveryOrders: number;
    pickupOrders: number;
    withQuote: number;
    quoteCoverage: number;
    shippingRevenue: number;
    avgShipping: number;
    shipmentsCreated?: number;
    shipmentsDelivered?: number;
    otifRate?: number;
    otifContractualRate?: number;
    otifDeliveredWithPromise?: number;
    otifByProvider?: Array<{
      provider: string;
      shipments: number;
      delivered: number;
      onTimeContractual: number;
      otifRate: number;
    }>;
    otifInFullRate?: number;
    otifInFullCount?: number;
    otifNotInFullCount?: number;
    otifIfUniverse?: number;
    otifCompositeRate?: number;
  };
  inventory?: {
    trackedActive: number;
    stockouts: number;
    lowStock: number;
    ingredientsActive?: number;
    ingredientsLowStock?: number;
    productsWithRecipe?: number;
    ingredientInventoryValue?: number;
  };
  automations?: {
    emailEvents: number;
    emailDelivered: number;
    emailOpened: number;
    emailClicked: number;
    openRate: number;
    cronJobs: number;
    cronEnabled: number;
  };
}

const PERIOD_LABELS: Record<Period, string> = {
  "7d": "Últimos 7 días",
  "30d": "Últimos 30 días",
  "90d": "Últimos 90 días",
  ytd: "Este año",
};

function fmt(n: number) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(n);
}

function pctDelta(
  current: number,
  prev: number,
): { label: string; up: boolean; zero: boolean } {
  if (prev === 0) {
    if (current === 0 && prev === 0) return { label: "0%", up: false, zero: true };
    if (current === 0) return { label: "-100%", up: false, zero: false };
    return { label: "+100%", up: true, zero: false };
  }
  const d = ((current - prev) / prev) * 100;
  return {
    label: `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`,
    up: d >= 0,
    zero: d === 0,
  };
}

function DeltaBadge({ current, prev }: { current: number; prev: number }) {
  const { label, up, zero } = pctDelta(current, prev);
  if (zero)
    return (
      <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        {label}
      </span>
    );
  return (
    <span
      className={cn(
        "flex items-center gap-0.5 text-xs font-medium",
        up ? "text-emerald-400" : "text-red-400",
      )}
    >
      {up ? (
        <ArrowUpRight className="h-3 w-3" />
      ) : (
        <ArrowDownRight className="h-3 w-3" />
      )}
      {label}
    </span>
  );
}

function KpiCard({
  label,
  value,
  sub,
  current,
  prev,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  current: number;
  prev: number;
  icon: React.ElementType;
}) {
  return (
    <div className="glass-card rounded-xl border border-border/60 p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
          {label}
        </span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
      <div className="flex items-center justify-between gap-2">
        <DeltaBadge current={current} prev={prev} />
        {sub && (
          <span className="text-[11px] text-muted-foreground">{sub}</span>
        )}
      </div>
    </div>
  );
}

function RevenueChart({
  rows,
  metric,
}: {
  rows: MetricsData["ordersByDay"];
  metric: "count" | "revenue";
}) {
  const vals = rows.map((r) => (metric === "count" ? r.count : r.revenue));
  const maxVal = Math.max(1, ...vals);
  const showLabels = rows.length <= 14;

  if (!rows.length || vals.every((v) => v === 0)) {
    return (
      <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
        Sin datos en el período seleccionado.
      </div>
    );
  }

  return (
    <div className="flex h-36 items-end gap-px overflow-hidden">
      {rows.map((row) => {
        const val = metric === "count" ? row.count : row.revenue;
        const pct = Math.max(4, (val / maxVal) * 100);
        return (
          <div
            key={row.day}
            className="group relative flex flex-1 flex-col items-center"
            style={{ minWidth: 0 }}
          >
            <div
              className="w-full rounded-t bg-primary/70 transition-all group-hover:bg-primary"
              style={{ height: `${pct}%` }}
            />
            {/* Tooltip */}
            <div className="pointer-events-none absolute bottom-full mb-1 hidden rounded bg-popover px-2 py-1 text-[10px] shadow group-hover:block whitespace-nowrap z-10">
              <p className="text-muted-foreground">{row.day.slice(5)}</p>
              <p className="font-medium">
                {metric === "count" ? `${val} pedidos` : fmt(val)}
              </p>
            </div>
            {showLabels && (
              <span className="mt-1 text-[9px] text-muted-foreground">
                {row.day.slice(5)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CustomerDonut({
  newC,
  returning,
}: {
  newC: number;
  returning: number;
}) {
  const total = newC + returning;
  if (total === 0)
    return (
      <p className="text-sm text-muted-foreground">Sin clientes en el período.</p>
    );
  const newPct = Math.round((newC / total) * 100);
  const retPct = 100 - newPct;
  const r = 30;
  const circumference = 2 * Math.PI * r;
  const newDash = (newPct / 100) * circumference;

  return (
    <div className="flex items-center gap-6">
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="12"
        />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="12"
          strokeDasharray={`${newDash} ${circumference - newDash}`}
          strokeDashoffset={circumference / 4}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
        />
        <text
          x="40"
          y="44"
          textAnchor="middle"
          fontSize="13"
          fontWeight="bold"
          fill="currentColor"
        >
          {newPct}%
        </text>
      </svg>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
          <span className="text-foreground font-medium">{newC} nuevos</span>
          <span className="text-muted-foreground">({newPct}%)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40 shrink-0" />
          <span className="text-foreground font-medium">
            {returning} recurrentes
          </span>
          <span className="text-muted-foreground">({retPct}%)</span>
        </div>
        <p className="text-xs text-muted-foreground">{total} clientes únicos</p>
      </div>
    </div>
  );
}

function MetricsPage() {
  const tid = useWorkspaceTenantId();
  const { productFlags } = useTenantContext();
  const [period, setPeriod] = useState<Period>("30d");
  const [data, setData] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartMetric, setChartMetric] = useState<"count" | "revenue">("count");
  const [spendInput, setSpendInput] = useState("");
  const [metaSpendInput, setMetaSpendInput] = useState("");
  const [googleSpendInput, setGoogleSpendInput] = useState("");
  const [spendSaving, setSpendSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // Ad spends historial
  const [adSpends, setAdSpends] = useState<AdSpend[]>([]);
  const [adSpendForm, setAdSpendForm] = useState({ date: new Date().toISOString().slice(0, 10), channel: "meta", amountCents: "", notes: "" });
  const [adSpendSaving, setAdSpendSaving] = useState(false);
  const [activeModule, setActiveModule] = useState<AnalyticsMetricModuleId>("executive");
  // SCRUM-74: el switcher global de sucursal (montado en app.tsx) ahora también
  // filtra el hub de métricas — antes cambiar de sucursal no afectaba estos KPIs.
  // Fase 8: reportLocationId permite ampliar la vista a "todas" (owner/super_admin/
  // staff sin scope) sin tocar la sucursal operativa activa del sidebar.
  const { reportLocationId, setReportLocationId, canViewAll, locations } = useReportingLocationScope();

  useEffect(() => {
    if (!tid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const metricsPath = reportLocationId
      ? `/api/metrics?period=${period}&locationId=${encodeURIComponent(reportLocationId)}`
      : `/api/metrics?period=${period}`;
    void apiGetJson<MetricsData>(metricsPath, tid)
      .then((d) => {
        setData(d);
        const units = marketingSpendCentsToUnits(d.marketingSpendCentsMonthly ?? null);
        setSpendInput(units != null ? String(units) : "");
        const metaU = marketingSpendCentsToUnits(d.adsSpendMetaCents ?? null);
        setMetaSpendInput(metaU != null ? String(metaU) : "");
        const googleU = marketingSpendCentsToUnits(d.adsSpendGoogleCents ?? null);
        setGoogleSpendInput(googleU != null ? String(googleU) : "");
      })
      .catch((e: unknown) => {
        setData(null);
        toast.error(e instanceof Error ? e.message : "No se pudieron cargar las métricas");
      })
      .finally(() => setLoading(false));
  }, [tid, period, reloadToken, reportLocationId]);

  // Cargar ad spends al montar
  useEffect(() => {
    if (!tid) return;
    apiGetJson<{ spends: AdSpend[] }>("/api/metrics/ad-spends", tid)
      .then((d) => setAdSpends(d.spends ?? []))
      .catch(() => {});
  }, [tid, reloadToken]);

  const addAdSpend = async () => {
    if (!tid) return;
    const amountCents = Math.round(Number(adSpendForm.amountCents) * 100);
    if (!adSpendForm.date || !adSpendForm.channel || !Number.isFinite(amountCents) || amountCents < 0) {
      toast.error("Completá fecha, canal y monto válido");
      return;
    }
    setAdSpendSaving(true);
    try {
      const res = await apiSendJson<{ spend: AdSpend }>("/api/metrics/ad-spends", "POST", {
        date: adSpendForm.date,
        channel: adSpendForm.channel,
        amountCents,
        notes: adSpendForm.notes || undefined,
      }, tid);
      setAdSpends((prev) => [res.spend, ...prev]);
      setAdSpendForm({ date: new Date().toISOString().slice(0, 10), channel: "meta", amountCents: "", notes: "" });
      toast.success("Gasto agregado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setAdSpendSaving(false);
    }
  };

  const removeAdSpend = async (id: string) => {
    if (!tid) return;
    try {
      await apiDelete(`/api/metrics/ad-spends?id=${encodeURIComponent(id)}`, tid);
      setAdSpends((prev) => prev.filter((s) => s.id !== id));
      toast.success("Gasto eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const saveMarketingSpend = async () => {
    if (!tid) return;
    const parseUnits = (raw: string) => {
      if (raw.trim() === "") return null;
      const n = Number(raw.replace(",", "."));
      if (!Number.isFinite(n) || n < 0) return undefined;
      return n;
    };
    const parsed = parseUnits(spendInput);
    const parsedMeta = parseUnits(metaSpendInput);
    const parsedGoogle = parseUnits(googleSpendInput);
    if (parsed === undefined || parsedMeta === undefined || parsedGoogle === undefined) {
      toast.error("Ingresá montos válidos en ARS");
      return;
    }
    const cents = marketingSpendUnitsToCents(parsed);
    const metaCents = marketingSpendUnitsToCents(parsedMeta);
    const googleCents = marketingSpendUnitsToCents(parsedGoogle);
    setSpendSaving(true);
    try {
      const cfg = await apiGetJson<{ config?: { tracking?: Record<string, unknown> } }>(
        "/api/tenant/config",
        tid,
      );
      const tracking = { ...(cfg.config?.tracking ?? {}) };
      tracking.marketingSpendCentsMonthly = cents;
      tracking.adsSpendMetaCents = metaCents;
      tracking.adsSpendGoogleCents = googleCents;
      await apiSendJson("/api/tenant/config", "PATCH", { tracking }, tid);
      toast.success("Gasto marketing guardado (base + Meta + Google)");
      setReloadToken((n) => n + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar el gasto");
    } finally {
      setSpendSaving(false);
    }
  };

  const totalOrders = (data?.ordersByStatus ?? []).reduce(
    (s, x) => s + x.count,
    0,
  );
  const cancelledOrders =
    (data?.ordersByStatus ?? []).find((x) => x.status === "CANCELADO")?.count ??
    0;
  const conversionRate =
    totalOrders > 0
      ? Math.round(((totalOrders - cancelledOrders) / totalOrders) * 100)
      : 0;

  // Página abierta en todos los planes; módulos Pro+ filtrados por metricGates.
  const analyticsAdvanced = productFlags.analytics_advanced !== false;
  const gates = data?.metricGates ?? {};
  const visibleModules = visibleAnalyticsModules(gates);

  useEffect(() => {
    if (!data?.metricGates) return;
    setActiveModule((prev) => resolveInitialAnalyticsModule(data.metricGates, prev));
  }, [data?.metricGates]);

  return (
    <div>
      <DashboardHeader
        title="Métricas"
        subtitle="Analytics nativo Structura — módulos según tu plan y perfil"
        layout="sticky"
        actions={
          <div className="flex items-center gap-2">
            {canViewAll && locations.length > 1 ? (
              <Select
                value={reportLocationId ?? "__all__"}
                onValueChange={(v) => setReportLocationId(v === "__all__" ? null : v)}
              >
                <SelectTrigger className="h-8 w-44 text-xs" aria-label="Vista de reporting por sucursal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="text-xs">
                    Todas las sucursales
                  </SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id} className="text-xs">
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Select
              value={period}
              onValueChange={(v) => setPeriod(v as Period)}
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(PERIOD_LABELS) as Period[]).map((k) => (
                  <SelectItem key={k} value={k} className="text-xs">
                    {PERIOD_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {!tid ? (
        <ClinicalPageShell>
          <p className="text-sm text-muted-foreground">Sin workspace activo.</p>
        </ClinicalPageShell>
      ) : loading ? (
        <PageSkeleton rows={8} />
      ) : (
        <ClinicalPageShell className="space-y-6">
          {!analyticsAdvanced && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-950 dark:text-amber-100"
              data-testid="analytics-starter-banner"
            >
              Plan Starter: ves resumen, ventas y productos. Embudo, cohortes y módulos Pro+ requieren upgrade.
            </div>
          )}
          <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Módulos Analytics">
            {visibleModules.map((m: AnalyticsModuleNavItem) => (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={activeModule === m.id}
                data-testid={`analytics-module-${m.id}`}
                onClick={() => setActiveModule(m.id)}
                className={cn(
                  "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  activeModule === m.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border/60 bg-card text-muted-foreground hover:text-foreground",
                )}
                title={m.description}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Placeholders para módulos sin tablero rico */}
          <div className={modulePanelClass(activeModule, "logistics")} data-analytics-module="logistics">
            <ClinicalSection title="Logística">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                  label="Envíos"
                  value={data?.logistics?.deliveryOrders ?? 0}
                  current={data?.logistics?.deliveryOrders ?? 0}
                  prev={0}
                  icon={Truck}
                  sub={`${data?.logistics?.pickupOrders ?? 0} retiros`}
                />
                <KpiCard
                  label="Con cotización"
                  value={`${data?.logistics?.quoteCoverage ?? 0}%`}
                  current={data?.logistics?.quoteCoverage ?? 0}
                  prev={0}
                  icon={Truck}
                  sub={`${data?.logistics?.withQuote ?? 0} con quote`}
                />
                <KpiCard
                  label="Ingreso envío"
                  value={fmt(data?.logistics?.shippingRevenue ?? 0)}
                  current={data?.logistics?.shippingRevenue ?? 0}
                  prev={0}
                  icon={Wallet}
                  sub={`Prom. ${fmt(data?.logistics?.avgShipping ?? 0)}`}
                />
                <KpiCard
                  label="Ticket envío medio"
                  value={fmt(data?.logistics?.avgShipping ?? 0)}
                  current={data?.logistics?.avgShipping ?? 0}
                  prev={0}
                  icon={TrendingUp}
                  sub="shipping_price_cents"
                />
                {(gates.kpiOtif !== false) && (
                <KpiCard
                  label="OTIF proxy"
                  value={`${data?.logistics?.otifRate ?? 0}%`}
                  current={data?.logistics?.otifRate ?? 0}
                  prev={0}
                  icon={Truck}
                  sub={`${data?.logistics?.shipmentsDelivered ?? 0}/${data?.logistics?.shipmentsCreated ?? 0} entregas`}
                />
                )}
                {(gates.kpiOtifContractual !== false) && (
                <KpiCard
                  label="OTIF contractual"
                  value={`${data?.logistics?.otifContractualRate ?? 0}%`}
                  current={data?.logistics?.otifContractualRate ?? 0}
                  prev={0}
                  icon={Truck}
                  sub={`${data?.logistics?.otifDeliveredWithPromise ?? 0} con promised_at`}
                />
                )}
                {(gates.kpiOtifInFull !== false) && (
                <KpiCard
                  label="In-Full (proxy)"
                  value={`${data?.logistics?.otifInFullRate ?? 0}%`}
                  current={data?.logistics?.otifInFullRate ?? 0}
                  prev={0}
                  icon={Truck}
                  sub={`${data?.logistics?.otifInFullCount ?? 0}/${data?.logistics?.otifIfUniverse ?? 0} ENTREGADO`}
                />
                )}
                {(gates.kpiOtifInFull !== false) && (
                <KpiCard
                  label="OTIF compuesto"
                  value={`${data?.logistics?.otifCompositeRate ?? 0}%`}
                  current={data?.logistics?.otifCompositeRate ?? 0}
                  prev={0}
                  icon={Truck}
                  sub="OT × IF (proxy order-level)"
                />
                )}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                OTIF proxy: vs ETA quote. Contractual: vs <code>promised_at</code>. In-Full: proxy order-level (ENTREGADO vs cancel/devuelto; sin qty por línea aún).
              </p>
              {(gates.kpiOtifContractual !== false) && (data?.logistics?.otifByProvider?.length ?? 0) > 0 ? (
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {data!.logistics!.otifByProvider!.map((row) => (
                    <li key={row.provider} className="flex justify-between gap-2 tabular-nums">
                      <span>{row.provider}</span>
                      <span>{row.otifRate}% · {row.onTimeContractual}/{row.delivered}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </ClinicalSection>
          </div>
          <div className={modulePanelClass(activeModule, "pos")} data-analytics-module="pos">
            <ClinicalSection title="POS / Caja">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                  label="Sesiones abiertas"
                  value={data?.pos?.openSessions ?? 0}
                  current={data?.pos?.openSessions ?? 0}
                  prev={0}
                  icon={Banknote}
                  sub="Ahora"
                />
                <KpiCard
                  label="Sesiones cerradas"
                  value={data?.pos?.sessionsClosed ?? 0}
                  current={data?.pos?.sessionsClosed ?? 0}
                  prev={0}
                  icon={Banknote}
                  sub="En el período"
                />
                <KpiCard
                  label="Tickets POS"
                  value={data?.pos?.tickets ?? 0}
                  current={data?.pos?.tickets ?? 0}
                  prev={0}
                  icon={ShoppingBasket}
                  sub={fmt(data?.pos?.ticketAvg ?? 0) + " ticket medio"}
                />
                <KpiCard
                  label="Ventas POS"
                  value={fmt(data?.pos?.revenue ?? 0)}
                  current={data?.pos?.revenue ?? 0}
                  prev={0}
                  icon={TrendingUp}
                  sub={
                    (data?.pos?.sessionsClosed ?? 0) > 0
                      ? `Dif. arqueo Σ ${fmt(data?.pos?.absDifference ?? 0)}`
                      : "Sin cierres en el período"
                  }
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Tickets = pedidos con sesión de caja (`pos_session_id`). Diferencia de arqueo = suma de |difference| en cierres del período.
              </p>
            </ClinicalSection>
          </div>
          <div className={modulePanelClass(activeModule, "inventory")} data-analytics-module="inventory">
            <ClinicalSection title="Inventario">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                <KpiCard
                  label="SKUs con stock"
                  value={data?.inventory?.trackedActive ?? 0}
                  current={data?.inventory?.trackedActive ?? 0}
                  prev={0}
                  icon={Package}
                  sub="track_stock activos"
                />
                <KpiCard
                  label="Stockouts"
                  value={data?.inventory?.stockouts ?? 0}
                  current={data?.inventory?.stockouts ?? 0}
                  prev={0}
                  icon={Package}
                  sub="stock ≤ 0"
                />
                <KpiCard
                  label="Bajo mínimo"
                  value={data?.inventory?.lowStock ?? 0}
                  current={data?.inventory?.lowStock ?? 0}
                  prev={0}
                  icon={Package}
                  sub="≤ stock_minimo"
                />
                {(gates.kpiBom !== false) && (
                <KpiCard
                  label="Insumos BOM"
                  value={data?.inventory?.ingredientsActive ?? 0}
                  current={data?.inventory?.ingredientsActive ?? 0}
                  prev={0}
                  icon={Package}
                  sub={`${data?.inventory?.ingredientsLowStock ?? 0} low stock`}
                />
                )}
                {(gates.kpiBom !== false) && (
                <KpiCard
                  label="SKUs con receta"
                  value={data?.inventory?.productsWithRecipe ?? 0}
                  current={data?.inventory?.productsWithRecipe ?? 0}
                  prev={0}
                  icon={Package}
                  sub="sm_recipes"
                />
                )}
                {(gates.kpiBom !== false) && (
                <KpiCard
                  label="Valor insumos"
                  value={fmt(data?.inventory?.ingredientInventoryValue ?? 0)}
                  current={data?.inventory?.ingredientInventoryValue ?? 0}
                  prev={0}
                  icon={Wallet}
                  sub="stock × costo"
                />
                )}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Snapshot actual de `sm_products` (no serie temporal). BOM light: insumos activos / low stock / productos con receta / valor inventario.
              </p>
            </ClinicalSection>
          </div>
          <div className={modulePanelClass(activeModule, "automations")} data-analytics-module="automations">
            <ClinicalSection title="Automatizaciones">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                  label="Email events"
                  value={data?.automations?.emailEvents ?? 0}
                  current={data?.automations?.emailEvents ?? 0}
                  prev={0}
                  icon={Mail}
                  sub="emails del período"
                />
                <KpiCard
                  label="Open rate"
                  value={`${data?.automations?.openRate ?? 0}%`}
                  current={data?.automations?.openRate ?? 0}
                  prev={0}
                  icon={Mail}
                  sub={`${data?.automations?.emailOpened ?? 0} opens`}
                />
                <KpiCard
                  label="Crons ON"
                  value={data?.automations?.cronEnabled ?? 0}
                  current={data?.automations?.cronEnabled ?? 0}
                  prev={0}
                  icon={Zap}
                  sub={`de ${data?.automations?.cronJobs ?? 0} jobs`}
                />
                <KpiCard
                  label="Clicks email"
                  value={data?.automations?.emailClicked ?? 0}
                  current={data?.automations?.emailClicked ?? 0}
                  prev={0}
                  icon={Zap}
                  sub="período"
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Aperturas y clicks se registran cuando el destinatario abre o hace click en el email.
              </p>
            </ClinicalSection>
          </div>
          <div className={modulePanelClass(activeModule, "categories")}>
            <ClinicalSection title="Categorías">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Vistas categoría</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{data?.funnel?.categoryViews ?? 0}</p>
                </div>
                <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Búsquedas</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{data?.funnel?.searches ?? 0}</p>
                </div>
              </div>
            </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, "executive")} data-analytics-module="executive">
          {/* KPIs principales */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Pedidos"
              value={data?.ordersPeriod ?? 0}
              icon={ShoppingBasket}
              current={data?.ordersPeriod ?? 0}
              prev={data?.ordersPrevPeriod ?? 0}
              sub={`${data?.ordersToday ?? 0} hoy`}
            />
            <KpiCard
              label="Revenue"
              value={fmt(data?.revenuePeriod ?? 0)}
              icon={TrendingUp}
              current={data?.revenuePeriod ?? 0}
              prev={data?.revenuePrevPeriod ?? 0}
            />
            <KpiCard
              label="Ticket promedio"
              value={fmt(data?.ticketPromedio ?? 0)}
              icon={Wallet}
              current={data?.ticketPromedio ?? 0}
              prev={data?.ticketPrevPeriod ?? 0}
              sub={`${data?.ordersPending ?? 0} pendientes`}
            />
            <KpiCard
              label="Clientes únicos"
              value={
                (data?.newCustomers ?? 0) + (data?.returningCustomers ?? 0)
              }
              icon={Users}
              current={
                (data?.newCustomers ?? 0) + (data?.returningCustomers ?? 0)
              }
              prev={0}
              sub={`${conversionRate}% completados`}
            />
          </div>


          <ClinicalSection title="Reseñas / NPS">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: "NPS enviados", value: data?.reviews?.npsSubmitted ?? 0 },
                { label: "Feedbacks", value: data?.reviews?.reviewSubmitted ?? 0 },
                { label: "Pedidos de reseña", value: data?.reviews?.reviewRequested ?? 0 },
                { label: "Clicks link Google", value: data?.reviews?.reviewLinkClicked ?? 0 },
                { label: "Pedidos por referido", value: data?.referralAttributedOrders ?? 0 },
                { label: "Growth impressions (mes)", value: data?.growthEventCounts?.impressions ?? 0 },
                { label: "Growth interactions (mes)", value: data?.growthEventCounts?.interactions ?? 0 },
              ].map((row) => (
                <div key={row.label} className="rounded-xl border border-border/60 bg-card px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{row.label}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{row.value}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Loop operable: link en Tracking + CTA checkout/thank-you + evento `review_link_clicked`.
              Google Reviews API (OAuth/Business Profile) sigue GAP.
            </p>
          </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, ["customers", "marketing"])} data-analytics-module="customers-marketing">
          {/* LTV nativo + CAC placeholder */}
          <ClinicalSection title="Valor de cliente (LTV)">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    LTV promedio
                  </p>
                  <HeartHandshake className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {fmt(data?.ltv?.average ?? 0)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  All-time · {data?.ltv?.customersWithOrders ?? 0} clientes con ≥1 pedido
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  LTV mediana
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {data?.ltv?.median != null ? fmt(data.ltv.median) : "—"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">All-time (menos sesgado por top)</p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  LTV del período
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {fmt(data?.ltv?.periodAverage ?? 0)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {PERIOD_LABELS[(data?.ltv?.periodLabel as Period) ?? period]} ·{" "}
                  {data?.ltv?.periodCustomers ?? 0} clientes
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card px-4 py-3" data-testid="cac-card">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">CAC</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {data?.cac != null ? fmt(data.cac) : "—"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Fórmula: <span className="font-medium text-foreground">CAC = gasto mensual ÷ clientes nuevos</span>
                  {" "}({data?.newCustomers ?? 0} en el período).
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {data?.cacNote ??
                    "Sin marketing spend nativo (PARTIAL). LTV disponible."}
                </p>
                <a
                  href="#marketing-spend-form"
                  className="mt-2 inline-block text-xs text-primary hover:underline"
                  data-testid="cac-spend-anchor"
                >
                  Cargar / editar gasto →
                </a>
              </div>
            </div>
            <div
              id="marketing-spend-form"
              className="mt-4 space-y-3 rounded-xl border border-dashed border-border/60 bg-muted/20 p-4"
              data-testid="marketing-spend-form"
            >
              <div className="space-y-1">
                <p className="text-xs font-semibold text-foreground">Gasto marketing (manual)</p>
                <p className="text-[11px] text-muted-foreground">
                  CAC usa la suma base + Meta + Google.{" "}
                  <span className="font-medium">Conectar ads (próximamente)</span> — sin OAuth Meta/Google Ads.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="marketing-spend">Base / otros (ARS)</Label>
                  <Input
                    id="marketing-spend"
                    type="number"
                    min={0}
                    step={100}
                    inputMode="decimal"
                    placeholder="Ej. 100000"
                    value={spendInput}
                    onChange={(e) => setSpendInput(e.target.value)}
                    data-testid="marketing-spend-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ads-spend-meta">Meta Ads (ARS)</Label>
                  <Input
                    id="ads-spend-meta"
                    type="number"
                    min={0}
                    step={100}
                    inputMode="decimal"
                    placeholder="Opcional"
                    value={metaSpendInput}
                    onChange={(e) => setMetaSpendInput(e.target.value)}
                    data-testid="ads-spend-meta-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ads-spend-google">Google Ads (ARS)</Label>
                  <Input
                    id="ads-spend-google"
                    type="number"
                    min={0}
                    step={100}
                    inputMode="decimal"
                    placeholder="Opcional"
                    value={googleSpendInput}
                    onChange={(e) => setGoogleSpendInput(e.target.value)}
                    data-testid="ads-spend-google-input"
                  />
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={spendSaving}
                onClick={() => void saveMarketingSpend()}
                data-testid="marketing-spend-save"
              >
                {spendSaving ? "Guardando…" : "Guardar gasto"}
              </Button>
            </div>

            {/* ── Historial de gastos ads ── */}
            <div className="mt-4 rounded-xl border border-border/60 bg-card p-4 space-y-3" id="ad-spends-table">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">Historial de gastos publicitarios</p>
                <p className="text-[11px] text-muted-foreground">
                  {adSpends.length > 0 ? `${adSpends.length} registro${adSpends.length !== 1 ? "s" : ""}` : "Sin registros"}
                </p>
              </div>
              {/* Formulario de carga */}
              <div className="grid gap-2 sm:grid-cols-5 items-end">
                <div className="space-y-1">
                  <Label className="text-[11px]">Fecha</Label>
                  <Input
                    type="date"
                    value={adSpendForm.date}
                    onChange={(e) => setAdSpendForm((f) => ({ ...f, date: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Canal</Label>
                  <select
                    value={adSpendForm.channel}
                    onChange={(e) => setAdSpendForm((f) => ({ ...f, channel: e.target.value }))}
                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  >
                    <option value="meta">Meta Ads</option>
                    <option value="google">Google Ads</option>
                    <option value="tiktok">TikTok Ads</option>
                    <option value="instagram">Instagram Ads</option>
                    <option value="youtube">YouTube Ads</option>
                    <option value="email_marketing">Email Marketing</option>
                    <option value="influencer">Influencer</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Monto (ARS)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={100}
                    placeholder="Ej. 15000"
                    value={adSpendForm.amountCents}
                    onChange={(e) => setAdSpendForm((f) => ({ ...f, amountCents: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Nota (opcional)</Label>
                  <Input
                    type="text"
                    placeholder="Campaña, detalle…"
                    value={adSpendForm.notes}
                    onChange={(e) => setAdSpendForm((f) => ({ ...f, notes: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
                <Button size="sm" className="h-8" disabled={adSpendSaving} onClick={() => void addAdSpend()}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
                </Button>
              </div>
              {/* Tabla de registros */}
              {adSpends.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border/50 text-left text-[11px] text-muted-foreground">
                        <th className="pb-1.5 pr-3">Fecha</th>
                        <th className="pb-1.5 pr-3">Canal</th>
                        <th className="pb-1.5 pr-3 text-right">Monto</th>
                        <th className="pb-1.5 pr-3">Nota</th>
                        <th className="pb-1.5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {adSpends.map((s) => {
                        const CHANNEL_LABELS: Record<string, string> = {
                          meta: "Meta Ads", google: "Google Ads", tiktok: "TikTok Ads",
                          instagram: "Instagram Ads", youtube: "YouTube Ads",
                          email_marketing: "Email Marketing", influencer: "Influencer", other: "Otro",
                        };
                        return (
                          <tr key={s.id} className="hover:bg-muted/30">
                            <td className="py-1.5 pr-3">{s.date}</td>
                            <td className="py-1.5 pr-3">{CHANNEL_LABELS[s.channel] ?? s.channel}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">
                              {new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(s.amountCents / 100)}
                            </td>
                            <td className="py-1.5 pr-3 text-muted-foreground">{s.notes ?? "—"}</td>
                            <td className="py-1.5">
                              <button
                                type="button"
                                onClick={() => void removeAdSpend(s.id)}
                                className="text-muted-foreground hover:text-destructive transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {/* CAC desde historial */}
              {adSpends.length > 0 && (data?.newCustomers ?? 0) > 0 && (
                <p className="text-[11px] text-muted-foreground border-t border-border/40 pt-2">
                  CAC historial (período):{" "}
                  <span className="font-semibold text-foreground">
                    {new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(
                      adSpends.reduce((sum, s) => sum + s.amountCents, 0) / 100 / (data?.newCustomers ?? 1)
                    )}
                  </span>{" "}
                  por cliente nuevo ({data?.newCustomers} en el período seleccionado, suma total de gastos registrados).
                </p>
              )}
            </div>

            {(data?.ltv?.buckets?.length ?? 0) > 0 && (
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {data?.ltv?.buckets?.map((b) => (
                  <span
                    key={b.label}
                    className="rounded-full border border-border/50 px-2.5 py-1 tabular-nums"
                  >
                    {b.label}: {b.count}
                  </span>
                ))}
              </div>
            )}
          </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, ["conversion", "storefront", "emails"])} data-analytics-module="conversion">
          {/* Embudo storefront nativo */}
          <ClinicalSection title="Embudo de conversión">
            {data?.funnel && (data.funnel.sessions > 0 || data.funnel.productViews > 0 || (data.funnel.pageViews ?? 0) > 0) ? (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  {[
                    { label: "Sesiones", value: data.funnel.sessions },
                    { label: "Page views", value: data.funnel.pageViews ?? 0 },
                    { label: "Vistas producto", value: data.funnel.productViews },
                    { label: "Add to cart", value: data.funnel.addToCarts },
                    { label: "Checkout", value: data.funnel.checkoutsStarted },
                    { label: "Pedidos", value: data.funnel.orders },
                  ].map((step) => (
                    <div
                      key={step.label}
                      className="rounded-xl border border-border/60 bg-card px-4 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {step.label}
                      </p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums">{step.value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>Vista→carrito {data.funnel.viewToCartRate}%</span>
                  <span>Carrito→checkout {data.funnel.cartToCheckoutRate}%</span>
                  <span>Checkout→pedido {data.funnel.checkoutToOrderRate}%</span>
                  <span>Sesión→pedido {data.funnel.sessionToOrderRate}%</span>
                  <span>Categorías {data.funnel.categoryViews ?? 0}</span>
                  <span>Búsquedas {data.funnel.searches ?? 0}</span>
                  <span>Sin resultado {data.funnel.searchZeros ?? 0}</span>
                  <span>Remove cart {data.funnel.removeFromCarts ?? 0}</span>
                  {(data.metricGates?.kpiBounce !== false) && (
                  <span>Bounce {data.funnel.bounces ?? 0} ({data.funnel.bounceRate ?? 0}%)</span>
                  )}
                  <span>Scroll events {data.funnel.scrollDepthEvents ?? 0}</span>
                  <span>Exits {data.funnel.pageExits ?? 0}</span>
                  <span>Hero CTA {data.funnel.heroCtaClicks ?? 0}</span>
                  <span>Banners {data.funnel.bannerClicks ?? 0}</span>
                  <span>Carousel {data.funnel.carouselViews ?? 0}</span>
                  <span>Wishlist {data.funnel.wishlistOpens ?? 0}</span>
                  <span>Cart updates {data.funnel.cartUpdates ?? 0}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Todavía no hay eventos de embudo en este período. Se registran solos al navegar
                el storefront y el widget. Conectá también pixels (GA4/Meta) en Configuración si
                querés atribución externa.
              </p>
            )}
          </ClinicalSection>

          {/* Checkout abandonado (PENDIENTE_PAGO + recovery emails) */}
          <ClinicalSection title="Checkout abandonado">
            {data?.abandonedCheckout ? (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    {
                      label: "Esperando pago",
                      value: data.abandonedCheckout.pendingPayment,
                    },
                    {
                      label: "Emails recovery",
                      value: data.abandonedCheckout.recoveryEmailsSent,
                    },
                    {
                      label: "Pendientes de recovery",
                      value: data.abandonedCheckout.awaitingRecovery,
                    },
                    {
                      label: "Tasa abandono",
                      value: `${data.abandonedCheckout.abandonedRate}%`,
                    },
                  ].map((step) => (
                    <div
                      key={step.label}
                      className="rounded-xl border border-border/60 bg-card px-4 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {step.label}
                      </p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums">{step.value}</p>
                    </div>
                  ))}
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {[
                    {
                      label: "Emails entregados",
                      value: data.abandonedCheckout.emailDelivered ?? 0,
                    },
                    {
                      label: "Open rate",
                      value: `${data.abandonedCheckout.openRate ?? 0}%`,
                      hint: `${data.abandonedCheckout.emailOpened ?? 0} opens`,
                    },
                    {
                      label: "Click rate",
                      value: `${data.abandonedCheckout.clickRate ?? 0}%`,
                      hint: `${data.abandonedCheckout.emailClicked ?? 0} clicks`,
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="rounded-xl border border-border/60 bg-card px-4 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {row.label}
                      </p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums">{row.value}</p>
                      {"hint" in row && row.hint ? (
                        <p className="mt-1 text-xs text-muted-foreground">{row.hint}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.abandonedCheckout.pendingRevenue > 0
                    ? `Hay ${fmt(data.abandonedCheckout.pendingRevenue)} en carritos sin completar. Los clientes reciben un email de recordatorio 2 horas después.`
                    : "No hay pedidos sin completar en este período."}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin datos de abandono en el período.</p>
            )}
          </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, "sales")} data-analytics-module="sales-trend">
          {/* Serie temporal */}
          <ClinicalSection
            title="Tendencia"
            actions={
              <div className="flex rounded-lg border border-border/60 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setChartMetric("count")}
                  className={cn(
                    "px-3 py-1 transition-colors",
                    chartMetric === "count"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/60",
                  )}
                >
                  Pedidos
                </button>
                <button
                  type="button"
                  onClick={() => setChartMetric("revenue")}
                  className={cn(
                    "px-3 py-1 transition-colors",
                    chartMetric === "revenue"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/60",
                  )}
                >
                  Revenue
                </button>
              </div>
            }
          >
            <RevenueChart rows={data?.ordersByDay ?? []} metric={chartMetric} />
          </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, "products")} data-analytics-module="products">
            <ClinicalSection title="Top productos">
              {!data?.topProducts.length ? (
                <p className="text-sm text-muted-foreground">
                  Sin ventas en el período.
                </p>
              ) : (
                <div className="space-y-2">
                  {data.topProducts.map((p, i) => {
                    const maxRev = data.topProducts[0].revenue;
                    const pct = maxRev > 0 ? (p.revenue / maxRev) * 100 : 0;
                    return (
                      <div key={p.name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span className="text-[11px] text-muted-foreground tabular-nums w-4">
                              {i + 1}
                            </span>
                            <span className="truncate max-w-[180px]">
                              {p.name}
                            </span>
                          </span>
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {p.qty % 1 ? p.qty.toFixed(2) : p.qty} u ·{" "}
                            {fmt(p.revenue)}
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-muted/50">
                          <div
                            className="h-1 rounded-full bg-primary/70"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, "customers")} data-analytics-module="customers">
            <ClinicalSection title="Clientes">
              <CustomerDonut
                newC={data?.newCustomers ?? 0}
                returning={data?.returningCustomers ?? 0}
              />
            </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, "sales")} data-analytics-module="sales-status">
          {/* Estado de órdenes */}
          <ClinicalSection title="Por estado">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(data?.ordersByStatus ?? [])
                .sort((a, b) => b.count - a.count)
                .map((s) => {
                  const pct =
                    totalOrders > 0
                      ? Math.round((s.count / totalOrders) * 100)
                      : 0;
                  const isCancelled = s.status === "CANCELADO";
                  return (
                    <div
                      key={s.status}
                      className="flex items-center justify-between rounded-lg border border-border/40 px-3 py-2 text-sm"
                    >
                      <span
                        className={cn(
                          "truncate",
                          isCancelled && "text-muted-foreground",
                        )}
                      >
                        {ORDER_STATUS_LABELS[s.status as OrderStatus] ??
                          s.status}
                      </span>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span
                          className={cn(
                            "font-semibold tabular-nums",
                            isCancelled
                              ? "text-muted-foreground"
                              : "text-foreground",
                          )}
                        >
                          {s.count}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {pct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </ClinicalSection>
          </div>

          <div className={modulePanelClass(activeModule, "customers")} data-analytics-module="customers-retention">
            <ClinicalSection title="Retención de clientes">
              <div className="glass-card rounded-xl border border-border/60 p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    Tasa de retención
                  </span>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <p
                  className={cn(
                    "text-2xl font-bold tabular-nums",
                    (data?.repeatCustomerRate ?? 0) >= 30
                      ? "text-emerald-400"
                      : (data?.repeatCustomerRate ?? 0) >= 10
                        ? "text-amber-400"
                        : "text-red-400",
                  )}
                >
                  {data?.repeatCustomerRate ?? 0}% clientes vuelven
                </p>
                <p className="text-xs text-muted-foreground">
                  {data?.repeatCustomerCount ?? 0} de {data?.totalUniqueCustomers ?? 0} clientes únicos realizaron más de un pedido en el período
                </p>
              </div>
            </ClinicalSection>

            {/* RFM simple — recencia de última compra */}
            {((data?.rfm?.active ?? 0) + (data?.rfm?.atRisk ?? 0) + (data?.rfm?.hibernating ?? 0)) > 0 && (
              <ClinicalSection title="Clientes por recencia (RFM)">
                <div className="grid gap-3 sm:grid-cols-3">
                  {[
                    {
                      label: "Activos",
                      hint: "Compraron en los últimos 30 días",
                      value: data?.rfm?.active ?? 0,
                      tone: "text-emerald-400",
                    },
                    {
                      label: "En riesgo",
                      hint: "Última compra hace 31–90 días",
                      value: data?.rfm?.atRisk ?? 0,
                      tone: "text-amber-400",
                    },
                    {
                      label: "Hibernando",
                      hint: "Sin compras hace más de 90 días",
                      value: data?.rfm?.hibernating ?? 0,
                      tone: "text-red-400",
                    },
                  ].map((bucket) => (
                    <div
                      key={bucket.label}
                      className="rounded-xl border border-border/60 bg-card px-4 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {bucket.label}
                      </p>
                      <p className={cn("mt-1 text-2xl font-semibold tabular-nums", bucket.tone)}>
                        {bucket.value}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">{bucket.hint}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Basado en la última orden no cancelada por email de cliente. Ideal para reactivar
                  segmentos en riesgo antes de campañas avanzadas.
                </p>
              </ClinicalSection>
            )}
          </div>

          <div className={modulePanelClass(activeModule, "salon")} data-analytics-module="salon">
            {(data?.qrMesa?.scans ?? 0) > 0 && (
              <ClinicalSection title="Canal QR mesa">
                <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Escaneos QR
                  </p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{data?.qrMesa?.scans ?? 0}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sesiones abiertas desde `/market/table/:token` (utm_source=qr_mesa). El embudo
                    marca channel <code>qr_mesa</code> en la experiencia de mesa.
                  </p>
                </div>
              </ClinicalSection>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Período: {data?.periodStart} → {data?.periodEnd} ·{" "}
            <Link
              to={"/app/pedidos" as never}
              className="text-primary hover:underline"
            >
              Ver todos los pedidos
            </Link>
          </p>
        </ClinicalPageShell>
      )}
    </div>
  );
}
